import { ReactNode } from 'react';

import { useComponentMutations } from 'approot/activity-builder/activity-builder.hooks';
import { SettingsComponentMenu } from 'approot/activity-builder/settings/settings-component-menu';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { ComponentFieldWrapper } from '../components.style';
import {
  ACTIVITIES_COMPONENT_TYPE_AUDIO,
  ACTIVITIES_COMPONENT_TYPE_DOCUMENT,
  ACTIVITIES_COMPONENT_TYPE_EBOOK,
  ACTIVITIES_COMPONENT_TYPE_EBOOK_INTERACTIVE,
  ACTIVITIES_COMPONENT_TYPE_GUIDED_RESEARCH,
  ACTIVITIES_COMPONENT_TYPE_IMAGE,
  ACTIVITIES_COMPONENT_TYPE_IMAGE_SPHERE,
  ACTIVITIES_COMPONENT_TYPE_INFO_BOX,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE,
  ACTIVITIES_COMPONENT_TYPE_LINK,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_MULTIPLE_CHOICE,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_RESPONSE,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_EQUATION_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_SLIDESHOW,
  ACTIVITIES_COMPONENT_TYPE_VIDEO,
  ACTIVITIES_COMPONENT_TYPE_WEBTOOL,
  ACTIVITIES_COMPONENT_TYPE_VOCABULARY,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_FILL_IN_BLANKS_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_DRAW,
  ACTIVITIES_COMPONENT_TYPE_TABLE,
  ACTIVITIES_COMPONENT_TYPE_DISCUSSION_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE_CATEGORISE,
  ActivitiesContentType,
  ACTIVITIES_COMPONENT_TYPE_RIVE_ANIMATION,
  ACTIVITIES_COMPONENT_TYPE_GIF,
} from 'global/constants';
import {
  ComponentAudio,
  ComponentColumn,
  ComponentDocument,
  ComponentEbook,
  ComponentEbookInteractive,
  ComponentGuidedResearch,
  ComponentImage,
  ComponentImageSphere,
  ComponentInfoBox,
  ComponentInteractive,
  ComponentLink,
  ComponentEquationResponse,
  ComponentMultipleChoice,
  ComponentResponse,
  ComponentSlideshow,
  ComponentVideo,
  ComponentVocabulary,
  ComponentFillInBlanks,
  ComponentDraw,
  ComponentTable,
  ComponentDiscussion,
  ComponentInteractiveResponseSequence,
  ComponentInteractiveResponseCategorise,
  ComponentRiveAnimation,
  ComponentGif,
} from '../component.constants';
import InfoBoxEdit from '../info-box/info-box-edit';
import InfoBoxSettings from '../info-box/info-box-settings';
import VideoEdit from '../video/video-edit';
import VideoSettings from '../video/video-settings';
import AudioEdit from '../audio/audio-edit';
import AudioSettings from '../audio/audio-settings';
import ResponseEdit from '../response/response-edit';
import ResponseSettings from '../response/response-settings';
import EquationResponseEdit from '../equation-response/equation-response-edit';
import EquationResponseSettings from '../equation-response/equation-response-settings';
import ImageEdit from '../image/image-edit';
import ImageSettings from '../image/image-settings';
import ImageSphereEdit from '../image-sphere/image-sphere-edit';
import ImageSphereSettings from '../image-sphere/image-sphere-settings';
import LinkEdit from '../link/link-edit';
import LinkSettings from '../link/link-settings';
import WebToolEdit from '../webtool/webtool-edit';
import WebToolSettings from '../webtool/webtool-settings';
import GuidedResearchEdit from '../guided-research/guided-research-edit';
import GuidedResearchSettings from '../guided-research/guided-research-settings';
import MultipleChoiceEdit from '../multiple-choice/multiple-choice-edit';
import MultipleChoiceSettings from '../multiple-choice/settings/multiple-choice-settings';
import EbookEdit from '../ebook/ebook-edit';
import EbookSettings from '../ebook/ebook-settings';
import DocumentEdit from '../document/document-edit';
import DocumentSettings from '../document/document-settings';
import InteractiveEdit from '../interactive/interactive-edit';
import InteractiveSettings from '../interactive/interactive-settings';
import InteractiveEbookEdit from '../interactive-ebook/interactive-ebook-edit';
import InteractiveEbookSettings from '../interactive-ebook/interactive-ebook-settings';
import SlideshowEditWrapper from '../slideshow/slideshow-edit';
import SlideshowSettings from '../slideshow/slideshow-settings';
import { ColumnWrapper } from './columns.style';
import { Draggable } from 'approot/activity-builder/drag-drop/draggable';
import { Droppable } from 'approot/activity-builder/drag-drop/droppable';
import { AddComponentTemplate } from '../components-menu/components-menu.constants';
import type { InteractiveLessonSectionComponentFragment } from '../../lesson-builder.graphql.generated';
import { getMoveComponentAriaLabel } from '../components.utils';
import {
  sortComponents,
  generateKeyBasedOnSort,
} from 'approot/activity-builder/activity-builder.utils';
import { ColumnComponentAddMenuTarget } from './column-add-menu';
import VocabularyEdit from '../vocabulary/vocabulary-edit';
import VocabularySettings from '../vocabulary/settings/vocabulary-settings';
import FillInBlanksEdit from '../fill-in-blanks/fill-in-blanks-edit';
import FillInBlanksSettings from '../fill-in-blanks/settings/fill-in-blanks-settings';
import DrawEdit from '../draw/draw-edit';
import DrawSettings from '../draw/settings/draw-settings';
import TableEdit from '../table/table-edit';
import TableSettings from '../table/table-settings';
import DiscussionEdit from '../discussion/discussion-edit';
import DiscussionSettings from '../discussion/discussion-settings';
import InteractiveResponseEdit from '../interactive-response/sequence/interactive-response-edit';
import InteractiveResponseSettings from '../interactive-response/sequence/settings/interactive-response-settings';
import InteractiveResponseCategoriseEdit from '../interactive-response/categorise/interactive-response-categorise-edit';
import InteractiveResponseCategoriseSettings from '../interactive-response/categorise/settings/interactive-response-categorise-settings';
import {
  ComponentConfirmDelete,
  useComponentConfirmDelete,
} from '../shared/component-confirm/component-confirm';
import { hasImage } from '../image/image.utils';
import RiveEdit from '../rive/rive-edit';
import RiveSettings from '../rive/settings/rive-settings';
import GifEdit from '../gif/gif-edit';
import GifSettings from '../gif/gif-settings';
import { ACTIVITY_BUILDER_DRAWER_EMPTY_EDIT } from 'approot/activity-builder/activity-builder.constants';
import IconTrash from 'approot/shared/icons/icon-trash';
import IconArrowUp from 'approot/shared/icons/icon-arrow-up';
import IconArrowDown from 'approot/shared/icons/icon-arrow-down';
import {
  getAppliedColour,
  isBackgroundApplied,
  isBorderApplied,
} from 'approot/activity-builder/components/shared/component-settings-borders.utils';
import { getStyleProperties } from 'approot/activity-builder/components/components.utils';

type Props = {
  id: string;
  sectionId: string;
  column: ComponentColumn;
  contentId: number;
  contentType: ActivitiesContentType;
  lessonId: number;
  settings: ReactNode;
  addComponent: (props: AddComponentTemplate, index: number) => void;
};

export const ColumnsComponentsWrapper = ({
  id,
  sectionId,
  column,
  contentId,
  contentType,
  lessonId,
  addComponent,
  settings: columnSettings,
}: Props) => {
  const sortedComponents = sortComponents(column.components);

  const {
    activeComponent,
    setActiveComponent,
    setComponentDrawerId,
    isEditDrawerActive,
    isAddDrawerActive,
    closeDrawer,
  } = useActivityBuilderContext();

  const { handleMoveDown, handleMoveUp, handleDelete } = useComponentMutations({
    sectionId,
    components: sortedComponents,
  });

  const { deleteComponentId, componentToDelete, setDeleteComponentId } =
    useComponentConfirmDelete({ components: sortedComponents });

  if (!sortedComponents) return null;

  return (
    <ColumnWrapper data-selector="column-wrapper">
      {deleteComponentId && componentToDelete && (
        <ComponentConfirmDelete
          component={componentToDelete}
          handleClose={() => setDeleteComponentId(null)}
          handleConfirm={() => {
            handleDelete(componentToDelete);

            // if the edit drawer is active set to empty
            if (isEditDrawerActive()) {
              setActiveComponent(undefined);
              setComponentDrawerId(ACTIVITY_BUILDER_DRAWER_EMPTY_EDIT);
            } else if (isAddDrawerActive()) {
              // close the add drawer as picking a new target is harder
              closeDrawer();
            }
          }}
        />
      )}

      {sortedComponents.map((component, index) => {
        return (
          <div
            key={generateKeyBasedOnSort({
              id: component.id,
              components: sortedComponents,
            })}
          >
            {index === 0 && (
              <Droppable
                nextComponentId={component.id}
                sectionId={sectionId}
                sortedComponents={sortedComponents}
                column={{
                  id: column.columnId,
                  componentId: id,
                }}
              />
            )}
            <Draggable
              id={component.id}
              type={component.type}
              typename={component.__typename}
              index={index}
              column={{
                id: column.columnId,
                componentId: id,
              }}
              sectionId={sectionId}
              sortedComponents={sortedComponents}
              onClick={(e, parentElement, clickedComponentId) => {
                e.relatedTarget = e.currentTarget;

                // handle portalled elements events since in React
                // they follow the React Tree not the DOM Tree
                if (parentElement.contains(e.target as Node)) {
                  if (activeComponent?.id !== clickedComponentId) {
                    setActiveComponent({
                      id: clickedComponentId,
                      column: {
                        id: column.columnId,
                        componentId: id, // this is column component id
                      },
                    });
                  }

                  if (isEditDrawerActive()) {
                    setComponentDrawerId(clickedComponentId);
                  }
                }
              }}
            >
              <ColumnComponent
                component={component}
                columnComponentId={id}
                columnId={column.columnId}
                contentId={contentId}
                contentType={contentType}
                lessonId={lessonId}
                render={({ component: comp, settings, customSettingsMenu }) => {
                  return (
                    <>
                      {activeComponent?.id === component.id && (
                        <SettingsComponentMenu
                          componentId={component.id}
                          componentSettings={settings}
                          type={component.type}
                          hasImage={
                            !!(
                              component.type ===
                                ACTIVITIES_COMPONENT_TYPE_IMAGE &&
                              hasImage(component)
                            )
                          }
                          columnComponentId={id}
                          columnSettings={columnSettings}
                          customSettingsMenu={customSettingsMenu}
                          addComponent={props => addComponent(props, index)}
                          options={[
                            ...(index > 0
                              ? [
                                  {
                                    onClick: () =>
                                      handleMoveUp(component, index),
                                    ariaLabel: getMoveComponentAriaLabel(
                                      component.type,
                                      'up',
                                      0
                                    ),
                                    icon: (
                                      <IconArrowUp width={20} height={20} />
                                    ),
                                    divider:
                                      index + 1 === sortedComponents.length,
                                    tooltip: 'Move up',
                                  },
                                ]
                              : []),
                            ...(index + 1 < sortedComponents.length
                              ? [
                                  {
                                    onClick: () =>
                                      handleMoveDown(component, index),
                                    ariaLabel: getMoveComponentAriaLabel(
                                      component.type,
                                      'down',
                                      0
                                    ),
                                    icon: (
                                      <IconArrowDown width={20} height={20} />
                                    ),
                                    divider: true,
                                    tooltip: 'Move down',
                                  },
                                ]
                              : []),
                            {
                              onClick: () => setDeleteComponentId(component.id),
                              ariaLabel: `Remove this ${component.type} component`,
                              icon: <IconTrash width={20} height={20} />,
                              tooltip: 'Delete',
                            },
                          ]}
                        />
                      )}
                      {comp}
                    </>
                  );
                }}
              />
            </Draggable>
            <Droppable
              previousComponentId={component.id}
              nextComponentId={sortedComponents[index + 1]?.id}
              column={{
                id: column.columnId,
                componentId: id,
              }}
              sectionId={sectionId}
              sortedComponents={sortedComponents}
            />
            <ColumnComponentAddMenuTarget
              isLastItem={index !== sortedComponents.length - 1}
              componentId={component.id}
              columnId={column.columnId}
              columnComponentId={id}
              addComponent={props => addComponent(props, index)}
            />
          </div>
        );
      })}
    </ColumnWrapper>
  );
};

type ColumnComponentProps = {
  component: InteractiveLessonSectionComponentFragment;
  columnComponentId: string;
  columnId: string;
  contentId: number;
  contentType: ActivitiesContentType;
  lessonId: number;
  render: (props: {
    component: ReactNode;
    settings?: ReactNode;
    customSettingsMenu?: ReactNode;
  }) => void;
};

const ColumnComponent = ({
  component,
  columnComponentId,
  columnId,
  render,
  contentId,
  contentType,
  lessonId,
}: ColumnComponentProps) => {
  const columnsData = {
    columnComponentId,
    columnId,
  };

  return (
    <ComponentFieldWrapper
      $colours={getAppliedColour(getStyleProperties(component))}
      $showBorder={isBorderApplied(getStyleProperties(component))}
      $showBackground={isBackgroundApplied(getStyleProperties(component))}
    >
      <>
        {component.type === ACTIVITIES_COMPONENT_TYPE_INFO_BOX &&
          render({
            component: <InfoBoxEdit {...(component as ComponentInfoBox)} />,
            settings: (
              <InfoBoxSettings
                {...(component as ComponentInfoBox)}
                columnsData={columnsData}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_RESPONSE &&
          render({
            component: <ResponseEdit {...(component as ComponentResponse)} />,
            settings: (
              <ResponseSettings
                {...(component as ComponentResponse)}
                contentId={contentId}
              />
            ),
          })}

        {component.type ===
          ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_MULTIPLE_CHOICE &&
          render({
            component: (
              <MultipleChoiceEdit {...(component as ComponentMultipleChoice)} />
            ),
            settings: (
              <MultipleChoiceSettings
                {...(component as ComponentMultipleChoice)}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_EBOOK &&
          render({
            component: (
              <EbookEdit
                {...(component as ComponentEbook)}
                lessonId={lessonId}
              />
            ),
            settings: (
              <EbookSettings
                {...(component as ComponentEbook)}
                lessonId={lessonId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_DOCUMENT &&
          render({
            component: (
              <DocumentEdit
                {...(component as ComponentDocument)}
                lessonId={lessonId}
              />
            ),
            settings: (
              <DocumentSettings
                {...(component as ComponentDocument)}
                lessonId={lessonId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_VIDEO &&
          render({
            component: (
              <VideoEdit
                {...(component as ComponentVideo)}
                lessonId={lessonId}
              />
            ),
            settings: (
              <VideoSettings
                {...(component as ComponentVideo)}
                lessonId={lessonId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_AUDIO &&
          render({
            component: (
              <AudioEdit
                {...(component as ComponentAudio)}
                lessonId={lessonId}
              />
            ),
            settings: (
              <AudioSettings
                {...(component as ComponentAudio)}
                lessonId={lessonId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_IMAGE &&
          render({
            component: (
              <ImageEdit
                {...(component as ComponentImage)}
                lessonId={lessonId}
                contentId={contentId}
              />
            ),
            settings: (
              <ImageSettings
                {...(component as ComponentImage)}
                lessonId={lessonId}
                contentId={contentId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_IMAGE_SPHERE &&
          render({
            component: (
              <ImageSphereEdit
                {...(component as ComponentImageSphere)}
                contentId={contentId}
              />
            ),
            settings: (
              <ImageSphereSettings
                {...(component as ComponentImageSphere)}
                contentId={contentId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_INTERACTIVE &&
          render({
            component: (
              <InteractiveEdit {...(component as ComponentInteractive)} />
            ),
            settings: (
              <InteractiveSettings
                {...(component as ComponentInteractive)}
                lessonId={lessonId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_EBOOK_INTERACTIVE &&
          render({
            component: (
              <InteractiveEbookEdit
                {...(component as ComponentEbookInteractive)}
              />
            ),
            settings: (
              <InteractiveEbookSettings
                {...(component as ComponentEbookInteractive)}
                lessonId={lessonId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_LINK &&
          render({
            component: (
              <LinkEdit {...(component as ComponentLink)} lessonId={lessonId} />
            ),
            settings: (
              <LinkSettings
                {...(component as ComponentLink)}
                lessonId={lessonId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_WEBTOOL &&
          render({
            component: (
              <WebToolEdit
                {...(component as ComponentLink)}
                lessonId={lessonId}
              />
            ),
            settings: (
              <WebToolSettings
                {...(component as ComponentLink)}
                lessonId={lessonId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_GUIDED_RESEARCH &&
          render({
            component: (
              <GuidedResearchEdit
                {...(component as ComponentGuidedResearch)}
                lessonId={lessonId}
              />
            ),
            settings: (
              <GuidedResearchSettings
                {...(component as ComponentGuidedResearch)}
                lessonId={lessonId}
              />
            ),
          })}

        {component.type ===
          ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_EQUATION_RESPONSE &&
          render({
            component: (
              <EquationResponseEdit
                {...(component as ComponentEquationResponse)}
                contentId={contentId}
              />
            ),
            settings: (
              <EquationResponseSettings
                {...(component as ComponentEquationResponse)}
                contentId={contentId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_SLIDESHOW &&
          render({
            component: (
              <SlideshowEditWrapper
                contentId={contentId}
                {...(component as ComponentSlideshow)}
              />
            ),
            settings: (
              <SlideshowSettings
                {...(component as ComponentSlideshow)}
                contentId={contentId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_VOCABULARY &&
          render({
            component: (
              <VocabularyEdit {...(component as ComponentVocabulary)} />
            ),
            settings: (
              <VocabularySettings
                {...(component as ComponentVocabulary)}
                contentId={contentId}
                contentType={contentType}
              />
            ),
          })}

        {component.type ===
          ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_FILL_IN_BLANKS_RESPONSE &&
          render({
            component: (
              <FillInBlanksEdit {...(component as ComponentFillInBlanks)} />
            ),
            settings: (
              <FillInBlanksSettings {...(component as ComponentFillInBlanks)} />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_DRAW &&
          render({
            component: <DrawEdit {...(component as ComponentDraw)} />,
            settings: (
              <DrawSettings
                {...(component as ComponentDraw)}
                contentId={contentId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_TABLE &&
          render({
            component: <TableEdit {...(component as ComponentTable)} />,
            settings: <TableSettings {...(component as ComponentTable)} />,
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_DISCUSSION_RESPONSE &&
          render({
            component: (
              <DiscussionEdit {...(component as ComponentDiscussion)} />
            ),
            settings: (
              <DiscussionSettings {...(component as ComponentDiscussion)} />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE &&
          render({
            component: (
              <InteractiveResponseEdit
                {...(component as ComponentInteractiveResponseSequence)}
              />
            ),
            settings: (
              <InteractiveResponseSettings
                {...(component as ComponentInteractiveResponseSequence)}
              />
            ),
          })}

        {component.type ===
          ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE_CATEGORISE &&
          render({
            component: (
              <InteractiveResponseCategoriseEdit
                {...(component as ComponentInteractiveResponseCategorise)}
              />
            ),
            settings: (
              <InteractiveResponseCategoriseSettings
                {...(component as ComponentInteractiveResponseCategorise)}
                contentId={contentId}
              />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_RIVE_ANIMATION &&
          render({
            component: <RiveEdit {...(component as ComponentRiveAnimation)} />,
            settings: (
              <RiveSettings {...(component as ComponentRiveAnimation)} />
            ),
          })}

        {component.type === ACTIVITIES_COMPONENT_TYPE_GIF &&
          render({
            component: <GifEdit {...(component as ComponentGif)} />,
            settings: <GifSettings {...(component as ComponentGif)} />,
          })}
      </>
    </ComponentFieldWrapper>
  );
};

export default ColumnComponent;
